import { captureAnalyticsEvent } from '#utils/analytics/analytics-events.js';
import { forcesFeatureFlags } from './force-feature-flags.js';

import type { PostHog } from 'posthog-js';

const FLAG_LOAD_TIMEOUT_MS = 3000;
// Dev degrades fast when flags never arrive (e.g. offline, missing API key).
const DEV_FLAG_LOAD_TIMEOUT_MS = 500;

/**
 * Resolves a PostHog feature flag outside React (e.g. in a route `beforeLoad`).
 * Waits for the first flag load before answering, so a not-yet-fetched flag is
 * never mistaken for "off". An unknown flag or a load timeout fails closed,
 * unless {@link forcesFeatureFlags} is set. Timeouts are reported via
 * `feature_flag_resolution_timeout`.
 *
 * @param posthog - The PostHog client.
 * @param flag - The PostHog feature-flag key.
 * @returns Whether the flag is enabled.
 */
export async function resolveFeatureFlag(posthog: PostHog, flag: string): Promise<boolean> {
	const fallback = forcesFeatureFlags();
	const timeoutMs = import.meta.env.DEV ? DEV_FLAG_LOAD_TIMEOUT_MS : FLAG_LOAD_TIMEOUT_MS;
	return new Promise((resolve) => {
		let unsubscribe: (() => void) | undefined;
		const settle = (enabled: boolean): void => {
			clearTimeout(timeout);
			// PostHog invokes the handler synchronously when flags are already loaded, so this
			// can run before `onFeatureFlags` has returned the handle; unsubscribe once it has.
			queueMicrotask(() => unsubscribe?.());
			resolve(enabled);
		};
		const timeout = setTimeout(() => {
			captureAnalyticsEvent('feature_flag_resolution_timeout', { flag_key: flag, timeout_ms: timeoutMs });
			settle(fallback);
		}, timeoutMs);
		unsubscribe = posthog.onFeatureFlags(() => settle(posthog.isFeatureEnabled(flag) ?? fallback));
	});
}
